import { Button, Link, Text } from "@backstage/ui";
import { type SwaBundle, type SwaBundleStatus } from "@internal/backstage-plugin-avd-common";

import { BundleCard, BundleFact, BundlePage, GatedAction, StatusBadge } from "../bundle/bundlePage";
import { usePlatformAdmin } from "../identity/ownership";
import { StateCard } from "../layout/pageShell";

import { ConnectActions } from "./ConnectActions";
import { useMyWebApps } from "./myWebApps";
import { deleteWebAppPath } from "./swaEntitlement";
import { SWA_STATUS_MARKS, swaPageDescriptor } from "./swaPageDescriptor";

const STATUS_SENTENCE: Record<SwaBundleStatus, string | undefined> = {
	provisioning:
		"Setting up takes about 20 minutes. You'll get a notification in Backstage when it is ready; you can leave this page.",
	ready: undefined,
	deprovisioning:
		"Deleting takes a few minutes. You'll get a notification in Backstage when it is done; you can leave this page.",
	absent: undefined,
	failed: "The last setup or delete did not finish. The platform team can see what happened; set it up again with the same project number, or reach out if it keeps failing.",
};

/** One bundle: its status and the non-secret facts the registry holds. */
export function WebAppCard({ bundle, admin }: { bundle: SwaBundle; admin: boolean }) {
	const sentence = STATUS_SENTENCE[bundle.status];
	return (
		<BundleCard
			name={`Project ${bundle.projectNumber}`}
			mark={SWA_STATUS_MARKS[bundle.status]}
			status={
				sentence ? (
					<Text as="p" variant="body-small" color="secondary">
						{sentence}
					</Text>
				) : undefined
			}
			primary={
				bundle.status === "ready" && bundle.siteUrl ? (
					<GatedAction
						action={{ enabled: true }}
						label="Open site"
						href={bundle.siteUrl}
						variant="primary"
						external
					/>
				) : undefined
			}
			body={bundle.status === "ready" ? <ConnectActions bundle={bundle} /> : undefined}
			facts={
				<>
					{bundle.siteUrl && (
						<BundleFact label="Site">
							<Text as="span" variant="body-small">
								<Link
									href={bundle.siteUrl}
									target="_blank"
									rel="noopener noreferrer"
								>
									{bundle.siteUrl}
								</Link>{" "}
								(asks for the visitor password)
							</Text>
						</BundleFact>
					)}
					{bundle.postgresHost && (
						<BundleFact label="Database host" value={bundle.postgresHost} />
					)}
					{bundle.keyVaultUri && (
						<BundleFact label="Key Vault" value={bundle.keyVaultUri} />
					)}
					{/* Plain text: the colleague holds no role at resource-group scope (swa-bundle.md § Who may do what). */}
					{bundle.resourceGroup && (
						<BundleFact label="Resource group" value={bundle.resourceGroup} />
					)}
					{admin && bundle.runUrl && (
						<BundleFact label="Last run">
							<Text as="span" variant="body-small">
								<Link
									href={bundle.runUrl}
									target="_blank"
									rel="noopener noreferrer"
								>
									{bundle.runUrl}
								</Link>
							</Text>
						</BundleFact>
					)}
				</>
			}
			remove={
				bundle.status === "ready" || bundle.status === "failed" ? (
					<GatedAction
						action={{ enabled: true }}
						label="Delete"
						danger
						href={deleteWebAppPath(bundle.projectNumber)}
						aria-label={`Delete project ${bundle.projectNumber}`}
					/>
				) : undefined
			}
		/>
	);
}

/** The hero's status: one badge per status the colleague's web apps are in, with its count. */
function StatusCounts({ bundles }: { bundles: readonly SwaBundle[] }) {
	const counts = new Map<SwaBundleStatus, number>();
	for (const b of bundles) counts.set(b.status, (counts.get(b.status) ?? 0) + 1);
	return (
		<>
			{[...counts].map(([status, count]) => (
				<StatusBadge
					key={status}
					mark={{
						...SWA_STATUS_MARKS[status],
						label: `${count} ${SWA_STATUS_MARKS[status].label}`,
					}}
				/>
			))}
		</>
	);
}

/** The colleague's own web app bundles (owner-only: the registry serves the session's slug alone). */
export const MyWebAppsPage = () => {
	const state = useMyWebApps();
	const { admin } = usePlatformAdmin();

	if (state.loading) {
		return <BundlePage descriptor={swaPageDescriptor} title="" cards={[]} loading />;
	}

	// No list at all when the read failed: an empty one would claim there are no web apps.
	if (state.failed) {
		return (
			<BundlePage
				descriptor={swaPageDescriptor}
				title="Your web apps could not be loaded."
				cards={[]}
				state={
					<StateCard
						role="alert"
						title="Couldn't load your web apps"
						action={
							<Button variant="primary" onPress={state.retry}>
								Try again
							</Button>
						}
					>
						The registry did not answer just now. Try again in a moment.
					</StateCard>
				}
			/>
		);
	}

	// A deleted bundle keeps its row (a new setup reuses it) but is not one of the colleague's web apps.
	const shown = state.bundles.filter((b) => b.status !== "absent");
	const title =
		shown.length === 0
			? "No web apps yet."
			: `${shown.length} web app${shown.length === 1 ? "" : "s"}.`;

	return (
		<BundlePage
			descriptor={swaPageDescriptor}
			title={title}
			meta={shown.length > 0 ? <StatusCounts bundles={shown} /> : undefined}
			cards={shown.map((bundle) => (
				<WebAppCard key={bundle.bundleId} bundle={bundle} admin={admin} />
			))}
		/>
	);
};
